'use client'

import { useEffect, useRef, useState } from 'react'
import type { BlogAuthorShortDto } from '@/lib/api-client/metka-client'
import { Avatar } from './Avatar'
import { BT } from './tokens'

export function AuthorHoverCard({
    author,
    avatarSize = 28,
}: {
    author: BlogAuthorShortDto
    avatarSize?: number
}) {
    const [open, setOpen] = useState(false)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const rootRef = useRef<HTMLSpanElement | null>(null)

    const show = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current)
        setOpen(true)
    }
    const scheduleHide = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current)
        closeTimer.current = setTimeout(() => setOpen(false), 120)
    }

    useEffect(() => {
        if (!open) return
        const onDocClick = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onDocClick)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDocClick)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current)
    }, [])

    const name = author.name || 'Автор'

    return (
        <span
            ref={rootRef}
            className="relative inline-flex items-center"
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                onFocus={show}
                onBlur={scheduleHide}
                aria-expanded={open}
                aria-label={`Об авторе ${name}`}
                className="inline-flex items-center gap-2 rounded-[999px] outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#ea580c]"
                style={{ color: 'inherit', fontWeight: 500 }}
            >
                <Avatar src={author.avatarUrl} name={author.name} size={avatarSize} />
                <span style={{ color: BT.ink }}>{name}</span>
            </button>
            {open && (
                <span
                    role="dialog"
                    aria-label={`Профиль автора: ${name}`}
                    className="absolute left-0 top-full z-30 mt-2 w-[360px] animate-in fade-in"
                    style={{
                        background: BT.paper,
                        border: `1px solid ${BT.rule}`,
                        borderRadius: 6,
                        padding: 16,
                        boxShadow: '0 12px 32px rgba(11,16,32,0.10)',
                    }}
                    onMouseEnter={show}
                    onMouseLeave={scheduleHide}
                >
                    <span className="flex items-start gap-5">
                        <Avatar src={author.avatarUrl} name={author.name} size={96} />
                        <span className="flex min-w-0 flex-col">
                            <span
                                className="truncate"
                                style={{
                                    fontFamily: BT.fontSub,
                                    fontWeight: 600,
                                    color: BT.ink,
                                    fontSize: 15,
                                    lineHeight: 1.25,
                                }}
                            >
                                {name}
                            </span>
                            {author.title && (
                                <span
                                    className="mt-1 text-[13px] leading-[1.4]"
                                    style={{ color: BT.sub }}
                                >
                                    {author.title}
                                </span>
                            )}
                        </span>
                    </span>
                </span>
            )}
        </span>
    )
}
